import { useEffect, useRef, useState } from "react";
import { SCREENS, UrlConstants } from "../utils/CommonUtils/constants";
import * as Sentry from "@sentry/react";
import LoadingWrapper from "../screens/loadingWrapper/LoadingWrapper";
import StudentCohortResultScreen from "../screens/studentCohortResultScreen/StudentCohortResultScreen";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import getAssessmentSet from "../fetchApi/getAssessmentSet";
import useParamValues from "../utils/useParamValues";
import CustomErrorDialogBox from "../components/CustomErrorDialogBox/CustomErrorDialogBox";
import useTranslationSetup from "../utils/useTranslationSetup";
import useWebViewBridge from "../utils/useWebViewBridge";
import DemoResultsScreen from "../screens/DemoResultsScreen/DemoResultsScreen";
import AssessmentNotConductedScreen from "../screens/AssessmentNotConductedScreen/AssessmentNotConductedScreen";
import {
  failReasonFromStoredAudioQuality,
  getAudioQualityFlagsFromAssessment,
} from "../utils/audioQuality/storedAudioQuality";
import { captureNullCohort } from "../utils/helper";

const ResultFlow = () => {
  const { t } = useTranslationSetup();
  const { studentId, token, classNum, subjectId } = useParamValues();
  const { sendMessageToReactNative } = useWebViewBridge();
  const [activeScreen, setActiveScreen] = useState(
    SCREENS.RENDER_STUDENT_COHORT_RESULT_LOADING_SCREEN
  );
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [assessmentSetResponse, setAssessmentSetResponse] = useState(null);
  const [selectedAssessmentResult, setSelectedAssessmentResult] =
    useState(null);
  const [feedbackGivenIds, setFeedbackGivenIds] = useState({});
  const assessmentUrl = `${
    UrlConstants.BASE_URL + UrlConstants.BASE_PATH_URL + UrlConstants.ASSESSMENT
  }`;
  const [reload, setReload] = useState(true);
  const abortController = useRef(null);

  async function handleFetchAssessmentSet() {
    setReload(false);
    try {
      const response = await getAssessmentSet(
        `${assessmentUrl}/result/assessment-set?student_id=${studentId}&grade=${classNum}&subject_id=${subjectId}&include_previous_results=${UrlConstants.SHOW_COMPARISON}`,
        abortController,
        token
      );
      if (response?.cohort == null) {
        captureNullCohort({
          source: "assessment-set",
          studentId,
          classNum,
          subjectId,
          cohort: response?.cohort,
        });
        setActiveScreen(SCREENS.RENDER_STUDENT_NO_ASSESSMENT_RESULT_SCREEN);
        return;
      }
      setAssessmentSetResponse(response);
    } catch (error) {
      if (error.status === 404) {
        setActiveScreen(SCREENS.RENDER_STUDENT_NO_ASSESSMENT_RESULT_SCREEN);
      }
      Sentry.captureException(error);
      setErrorDialogOpen(true);
    }
  }

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
    setReload(true);
  };

  useEffect(() => {
    if (assessmentSetResponse) {
      setActiveScreen(SCREENS.RENDER_STUDENT_COHORT_RESULT_SCREEN);
    }
  }, [assessmentSetResponse]);

  useEffect(() => {
    if (reload) {
      handleFetchAssessmentSet();
    }
  }, [reload]);

  const handleShowDemoResult = (assessmentId) => {
    const resultData = assessmentSetResponse?.assessments_result.find(
      (assessment) => assessment.id === assessmentId
    );
    if (!resultData) return;

    const matchedPreviousResult =
      assessmentSetResponse?.previous_result?.assessments_result?.find(
        (a) => a.assessment_type === resultData.assessment_type
      ) || null;

    const mapAssessmentForUi = (assessment) => {
      if (!assessment) return null;
      return {
        ...assessment,
        qualityFailReason: failReasonFromStoredAudioQuality(
          getAudioQualityFlagsFromAssessment(assessment)
        ),
      };
    };

    setSelectedAssessmentResult({
      ...mapAssessmentForUi(resultData),
      previous_result: mapAssessmentForUi(matchedPreviousResult),
    });
    setActiveScreen(SCREENS.RENDER_STUDENT_ASSESSMENT_RESULT_SCREEN);
  };

  const handleShowStudentList = () => {
    sendMessageToReactNative("status=2");
  };

  const renderActiveScreen = () => {
    switch (activeScreen) {
      case SCREENS.RENDER_STUDENT_COHORT_RESULT_LOADING_SCREEN:
        return (
          <>
            <LoadingWrapper type="resultLoading" />
            <CustomErrorDialogBox
              header={t("something_went_wrong")}
              icon={
                <ErrorOutlineOutlinedIcon
                  style={{
                    color: "#D95959",
                    borderRadius: "10px",
                    marginTop: "16px",
                    width: "80px",
                    height: "80px",
                  }}
                />
              }
              primaryButtonText={t("retake")}
              secondaryButtonText={t("new_student")}
              isDialogOpen={errorDialogOpen}
              showPrimaryButton
              showSecondaryButton
              onClickPrimaryButton={handleRetryClick}
              onClickSecondaryButton={() =>
                sendMessageToReactNative("status=2")
              }
            />
          </>
        );
      case SCREENS.RENDER_STUDENT_COHORT_RESULT_SCREEN:
        return (
          <StudentCohortResultScreen
            assessmentSetResponse={assessmentSetResponse}
            onSelectAssessment={handleShowDemoResult}
          />
        );
      case SCREENS.RENDER_STUDENT_ASSESSMENT_RESULT_SCREEN:
        return (
          <DemoResultsScreen
            processedData={selectedAssessmentResult}
            goBack={() =>
              setActiveScreen(SCREENS.RENDER_STUDENT_COHORT_RESULT_SCREEN)
            }
            feedbackGivenIds={feedbackGivenIds}
            markFeedbackGiven={(id) =>
              setFeedbackGivenIds((prev) => ({
                ...prev,
                [id]: true,
              }))
            }
          />
        );
      case SCREENS.RENDER_STUDENT_NO_ASSESSMENT_RESULT_SCREEN:
        return (
          <AssessmentNotConductedScreen
            handleShowStudentList={handleShowStudentList}
          />
        );
      default:
        break;
    }
  };

  return <>{renderActiveScreen()}</>;
};

export default ResultFlow;
